import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { Affiliate } from '../types';

export default function AffiliateDetail() {
  const { id } = useParams<{ id: string }>();

  const { data: affiliate, isLoading } = useQuery({
    queryKey: ['affiliate', id],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: Affiliate }>(`/affiliates/${id}`);
      return response.data.data;
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['affiliate-stats', id],
    queryFn: async () => {
      const response = await api.get(`/affiliates/${id}/stats`);
      return response.data.data;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  if (!affiliate) {
    return <div>Affiliate not found</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <Link
          to="/affiliates"
          className="inline-flex items-center text-sm text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Affiliates
        </Link>
      </div>

      <div className="bg-[var(--bg-secondary)] shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-[var(--border-color)]">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium leading-6 text-[var(--text-primary)]">
                {affiliate.name}
              </h3>
              <p className="mt-1 text-sm text-[var(--text-muted)]">{affiliate.email}</p>
            </div>
            <span
              className={`badge ${
                affiliate.status === 'approved'
                  ? 'badge-success'
                  : affiliate.status === 'pending'
                  ? 'badge-warning'
                  : 'badge-danger'
              }`}
            >
              {affiliate.status}
            </span>
          </div>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-sm font-medium text-[var(--text-muted)]">Referral Code</dt>
              <dd className="mt-1 text-sm text-[var(--text-primary)]">
                <code className="bg-[var(--bg-tertiary)] px-2 py-1 rounded">{affiliate.referralCode}</code>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--text-muted)]">Tenant</dt>
              <dd className="mt-1 text-sm text-[var(--text-primary)]">{affiliate.tenant?.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--text-muted)]">Commission Plan</dt>
              <dd className="mt-1 text-sm text-[var(--text-primary)]">
                {affiliate.commissionPlan?.name || 'Not assigned'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--text-muted)]">Phone</dt>
              <dd className="mt-1 text-sm text-[var(--text-primary)]">{affiliate.phone || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--text-muted)]">Company</dt>
              <dd className="mt-1 text-sm text-[var(--text-primary)]">{affiliate.company || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--text-muted)]">Website</dt>
              <dd className="mt-1 text-sm text-[var(--text-primary)]">
                {affiliate.website ? (
                  <a href={affiliate.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    {affiliate.website}
                  </a>
                ) : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--text-muted)]">Payment Method</dt>
              <dd className="mt-1 text-sm text-[var(--text-primary)]">{affiliate.paymentMethod || 'Not set'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--text-muted)]">Created At</dt>
              <dd className="mt-1 text-sm text-[var(--text-primary)]">
                {new Date(affiliate.createdAt).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--text-muted)]">Last Login</dt>
              <dd className="mt-1 text-sm text-[var(--text-primary)]">
                {affiliate.lastLoginAt
                  ? new Date(affiliate.lastLoginAt).toLocaleDateString()
                  : 'Never'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-[var(--bg-secondary)] shadow rounded-lg px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-[var(--text-muted)]">Total Clicks</dt>
            <dd className="mt-1 text-3xl font-semibold text-[var(--text-primary)]">{stats.clicks}</dd>
          </div>
          <div className="bg-[var(--bg-secondary)] shadow rounded-lg px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-[var(--text-muted)]">Conversions</dt>
            <dd className="mt-1 text-3xl font-semibold text-[var(--text-primary)]">
              {stats.players + stats.traders}
            </dd>
          </div>
          <div className="bg-[var(--bg-secondary)] shadow rounded-lg px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-[var(--text-muted)]">Total Commissions</dt>
            <dd className="mt-1 text-3xl font-semibold text-[var(--text-primary)]">
              ${stats.totalCommissions?.toLocaleString() || 0}
            </dd>
          </div>
          <div className="bg-[var(--bg-secondary)] shadow rounded-lg px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-[var(--text-muted)]">Balance</dt>
            <dd className="mt-1 text-3xl font-semibold text-[var(--text-primary)]">
              ${stats.balance?.toLocaleString() || 0}
            </dd>
          </div>
        </div>
      )}
    </div>
  );
}
